import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useIntervalFn } from '@vueuse/core'
import { sdk } from '@/graphql/client'
import { useAuthStore } from '@/stores/auth'
import { processDashboard, type DashboardData } from '@/components/domain/dashboard/dashboard-process'

const POLL_INTERVAL_MS = 60_000

export function useDashboardData() {
    const auth = useAuthStore()
    const data = ref<DashboardData | null>(null)
    const loading = ref(true)
    const refreshing = ref(false)
    const error = ref<string | null>(null)
    const lastFetchedAt = ref<Date | null>(null)

    async function fetch(silent = false): Promise<void> {
        if (silent) refreshing.value = true
        try {
            const r = await sdk.AdminDashboardGet()
            const raw = r?.admin?.dashboard
            if (raw) {
                data.value = processDashboard(raw)
                lastFetchedAt.value = new Date()
                error.value = null
            }
        } catch (e: unknown) {
            error.value = (e as Error).message
        } finally {
            loading.value = false
            refreshing.value = false
        }
    }

    const refresh = () => fetch(true)

    let stop: (() => void) | null = null
    const stopWatch = watch(
        () => auth.invalidationKey,
        (curr, prev) => {
            if (curr !== prev) fetch(true).catch(() => {})
        }
    )

    onMounted(() => {
        fetch().catch(() => {})
        const { pause } = useIntervalFn(() => {
            if (document.visibilityState === 'visible') fetch(true).catch(() => {})
        }, POLL_INTERVAL_MS)
        stop = pause
    })
    onUnmounted(() => {
        stop?.()
        stopWatch()
    })

    return { data, loading, refreshing, error, lastFetchedAt, refresh }
}
